import React, { useState, useContext } from "react";
import cookie from "react-cookies";
import { useTranslation } from "react-i18next";

import { Ok } from "@airhelp/icons";
import { Button, Flex, Box, Text, Heading, useToast, useBreakpointValue } from "@chakra-ui/react";

import { authApi } from "api";
import { IResetPasswordSubmitData } from "api/auth/auth";
import config from "config/config";
import { USER_RETURN_AHPLUS_COOKIE } from "config/cookies";
import { SessionContext } from "contexts";
import { useLocale } from "contexts/LocaleContext/LocaleContextProvider";
import { ErrorBadge } from "elements";
import { useTrackEvent } from "utils/tracking/hooks";

const { resetPassword } = authApi;

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(SessionContext);
  const { locale } = useLocale();
  const { trackCtaClick } = useTrackEvent();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();

  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(false);

  const isDisabled = isSending || isSent || error;

  const saveCookieUserReturnUrl = () => {
    cookie.save(USER_RETURN_AHPLUS_COOKIE, "1", config.cookieOptions);
  };

  const resetPasswordUser = async (data: IResetPasswordSubmitData) => {
    setIsSent(false);
    setError(false);
    setIsSending(true);

    const resetPasswordData: IResetPasswordSubmitData = { email: data.email, languageCode: data.languageCode };
    const [error] = await resetPassword(resetPasswordData);

    setIsSending(false);
    setIsSent(true);

    if (error) {
      setError(true);
    } else {
      saveCookieUserReturnUrl();

      toast({
        title: t("my_account.details.password_reset_link_sent"),
        status: "success",
        duration: 4000,
        position: isMobile ? "bottom" : "top",
        isClosable: true,
        icon: <Ok boxSize={5} alignSelf="center" color="greyscale.100" />,
      });
    }
  };

  const handleChangePassword = () => {
    trackCtaClick("reset password", "my account page");

    !!user && resetPasswordUser({ email: user.email, languageCode: locale });
  };

  return (
    <Flex
      backgroundColor="greyscale.100"
      borderRadius="xl"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      mt={4}
      p={{ base: 4, md: 6 }}
      width="100%"
    >
      {error && (
        <Box mb={3}>
          <ErrorBadge data-testid="error-text" />
        </Box>
      )}
      <Flex
        width="100%"
        flexDirection={{ base: "column", md: "row" }}
        gap={6}
        justifyContent="space-between"
        flexShrink="none"
      >
        <Box>
          <Heading variant="h5" mb={1}>
            {t("my_account.details.change_your_password")}
          </Heading>
          <Text fontSize="md" color="primary.700">
            {t("my_account.details.password_reset_link")}
          </Text>
        </Box>
        <Button
          size="m"
          textAlign="center"
          onClick={handleChangePassword}
          isDisabled={isDisabled}
          isLoading={isSending}
          width={{ base: "100%", md: "auto" }}
          data-testid="change-password-button"
          whiteSpace="nowrap"
          alignSelf="center"
        >
          {t("common.change_password")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default ChangePassword;
